<section class="page">
  <header class="page-header">
    <div>
      <h1>Shopping partners</h1>
      <p>Earn marketplace partners — default cashback % and tier share of cashback as points</p>
    </div>
    <button class="btn btn-primary" type="button" (click)="openCreate()" [disabled]="saving()">
      Add partner
    </button>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  <div class="filters">
    <input
      type="search"
      [(ngModel)]="search"
      placeholder="Search partners"
      (keyup.enter)="searchNow()"
    />
    <select [(ngModel)]="filterCategoryId">
      <option value="">All categories</option>
      @for (c of categories(); track c.id) {
        <option [value]="c.id">{{ c.name }}</option>
      }
    </select>
    <select [(ngModel)]="filterFeatured">
      <option value="">Featured: any</option>
      <option value="1">Featured only</option>
      <option value="0">Not featured</option>
    </select>
    <button class="btn btn-secondary" type="button" (click)="searchNow()" [disabled]="loading()">
      Search
    </button>
  </div>

  @if (showForm()) {
    <div class="panel create-panel">
      <h2>{{ editingId() ? 'Edit partner' : 'Create partner' }}</h2>
      <form [formGroup]="form" (ngSubmit)="save()" class="create-form partner-form">
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" />
        </label>
        <label>
          <span>Slug</span>
          <input type="text" formControlName="slug" placeholder="auto from name if empty" />
        </label>
        <label>
          <span>Aggregator</span>
          <input type="text" formControlName="aggregator" placeholder="e.g. affiliate, impact, awin" />
        </label>
        <label>
          <span>Default cashback %</span>
          <input type="number" min="0" max="100" step="0.01" formControlName="default_cashback_percent" />
        </label>
        <label>
          <span>Category</span>
          <select formControlName="category_id">
            <option value="">None</option>
            @for (c of categories(); track c.id) {
              <option [value]="c.id">{{ c.name }}</option>
            }
          </select>
        </label>
        <label>
          <span>Tagline</span>
          <input type="text" formControlName="tagline" />
        </label>
        <label class="full">
          <span>Short description</span>
          <textarea formControlName="short_description" rows="2"></textarea>
        </label>
        <label class="full">
          <span>About</span>
          <textarea formControlName="about" rows="4"></textarea>
        </label>
        <label>
          <span>External URL (Shop now)</span>
          <input type="url" formControlName="external_url" />
        </label>
        <label>
          <span>Availability</span>
          <select formControlName="availability">
            <option value="online">Online only</option>
            <option value="in_store">In-store only</option>
            <option value="both">In-store &amp; Online</option>
          </select>
        </label>

        <div class="full image-field">
          <label>
            <span>Logo URL (or pick file below — uploads to S3 on Save)</span>
            <input type="text" formControlName="logo_url" placeholder="https://… or leave empty if uploading file" />
          </label>
          <input type="file" accept="image/*" (change)="onFileSelected($event, 'logo')" />
          @if (pendingFiles.logo) {
            <span class="muted">Queued: {{ pendingFiles.logo.name }}</span>
          }
          @if (previewFor('logo')) {
            <img class="preview" [src]="previewFor('logo')!" alt="Logo preview" />
          }
        </div>

        <div class="full image-field">
          <label>
            <span>Banner URL (or pick file — uploads to S3 on Save)</span>
            <input type="text" formControlName="banner_url" />
          </label>
          <input type="file" accept="image/*" (change)="onFileSelected($event, 'banner')" />
          @if (pendingFiles.banner) {
            <span class="muted">Queued: {{ pendingFiles.banner.name }}</span>
          }
          @if (previewFor('banner')) {
            <img class="preview banner" [src]="previewFor('banner')!" alt="Banner preview" />
          }
        </div>

        <div class="full image-field">
          <label>
            <span>Card image URL (or pick file — uploads to S3 on Save)</span>
            <input type="text" formControlName="card_image_url" />
          </label>
          <input type="file" accept="image/*" (change)="onFileSelected($event, 'card_image')" />
          @if (pendingFiles.card_image) {
            <span class="muted">Queued: {{ pendingFiles.card_image.name }}</span>
          }
          @if (previewFor('card_image')) {
            <img class="preview" [src]="previewFor('card_image')!" alt="Card preview" />
          }
        </div>

        <div class="full tier-shares">
          <span class="tier-shares-label">Tier share of cashback (%) — one per subscription plan</span>
          @if (!plans().length) {
            <p class="muted">No subscription plans loaded. Add plans under Subscriptions first.</p>
          } @else {
            <div class="tier-shares-grid">
              @for (plan of plans(); track plan.plan_key) {
                <label>
                  <span>{{ plan.name || plan.plan_key }} ({{ plan.plan_key }})</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    [ngModel]="tierShares[plan.plan_key]"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="setTierShare(plan.plan_key, $event)"
                  />
                </label>
              }
            </div>
          }
        </div>
        <label>
          <span>Sort order</span>
          <input type="number" formControlName="sort_order" />
        </label>

        <label class="checkbox">
          <input type="checkbox" formControlName="is_active" />
          <span>Active</span>
        </label>
        <label class="checkbox">
          <input type="checkbox" formControlName="show_on_mobile" />
          <span>Show on mobile</span>
        </label>
        <label class="checkbox">
          <input type="checkbox" formControlName="is_featured" />
          <span>Featured</span>
        </label>
        <label class="checkbox">
          <input type="checkbox" formControlName="is_popular" />
          <span>Popular</span>
        </label>
        <label class="checkbox">
          <input type="checkbox" formControlName="is_recommended" />
          <span>Recommended</span>
        </label>

        <label class="full">
          <span>Metadata (JSON object, optional)</span>
          <textarea formControlName="metadata_json" rows="3" placeholder='{"key":"value"}'></textarea>
        </label>

        <div class="actions full">
          <button class="btn btn-primary" type="submit" [disabled]="saving() || !!uploading()">
            {{ saving() ? 'Saving…' : 'Save' }}
          </button>
          <button class="btn btn-secondary" type="button" (click)="cancelForm()" [disabled]="saving()">
            Cancel
          </button>
        </div>
      </form>
    </div>
  }

  <div class="panel">
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Partner</th>
            <th>Category</th>
            <th>Cashback / tiers</th>
            <th>Flags</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="7" class="muted">Loading…</td></tr>
          } @else if (!rows().length) {
            <tr><td colspan="7" class="muted">No shopping partners yet</td></tr>
          } @else {
            @for (row of rows(); track row.id) {
              <tr>
                <td>
                  @if (row.logo_url) {
                    <img class="logo" [src]="row.logo_url" [alt]="row.name" />
                  } @else {
                    <span class="logo-placeholder">—</span>
                  }
                </td>
                <td>
                  <div class="name">{{ row.name }}</div>
                  <div class="muted mono">{{ row.slug }}</div>
                  @if (row.aggregator) {
                    <div class="muted">{{ row.aggregator }}</div>
                  }
                </td>
                <td>{{ row.category?.name || '—' }}</td>
                <td class="points-cell">{{ pointsLabel(row) }}</td>
                <td class="flags">
                  @if (row.is_featured) { <span class="chip">featured</span> }
                  @if (row.is_popular) { <span class="chip">popular</span> }
                  @if (row.is_recommended) { <span class="chip">recommended</span> }
                  @if (!row.is_featured && !row.is_popular && !row.is_recommended) { <span class="muted">—</span> }
                </td>
                <td>
                  <span class="badge" [attr.data-status]="row.is_active && row.show_on_mobile ? 'active' : 'pending'">
                    {{ row.is_active ? (row.show_on_mobile ? 'mobile' : 'hidden') : 'inactive' }}
                  </span>
                </td>
                <td class="row-actions">
                  <button class="btn btn-secondary btn-sm" type="button" (click)="openEdit(row)">Edit</button>
                  <button class="btn btn-ghost btn-sm" type="button" (click)="remove(row)" [disabled]="saving()">
                    Delete
                  </button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
    @if (total() > limit) {
      <div class="pager">
        <span class="muted">Page {{ page() }} / {{ totalPages() }} ({{ total() }} total)</span>
        <div class="pager-actions">
          <button class="btn btn-secondary btn-sm" type="button" [disabled]="page() <= 1 || loading()" (click)="load(page() - 1)">
            Prev
          </button>
          <button class="btn btn-secondary btn-sm" type="button" [disabled]="page() >= totalPages() || loading()" (click)="load(page() + 1)">
            Next
          </button>
        </div>
      </div>
    }
  </div>
</section>
